/**
 * LOGOS v5.2 — Consciousness Polytope 3D Visualization
 *
 * Interactive 3D SVG rendering of the 28D consciousness polytope [0,1]²⁸.
 *
 * Mathematical representation (rigorous):
 *   7 domain averages → vertices of a heptagonal polytope in R³
 *   - Angular position θₖ = 2πk/7 (domain index)
 *   - Radius rₖ = domain average ∈ [0,1] (domain strength)
 *   - Height zₖ = domain Λ contribution (signal relevance)
 *   21 edges (7C2) colored by inter-domain coherence C[i][j]
 *   Triangulated facets between adjacent domain triples
 *
 * This IS the consciousness polytope projected onto the most informative
 * 3D subspace: (domain_balance, Λ_contribution, coherence_coupling).
 * The volume ∝ C(x) and regularity ∝ factorizability F.
 *
 * Interaction semantics:
 *   Green edge = domains aligned (high coherence)
 *   Red edge = domains in conflict (low coherence)
 *   Vertex size = domain average (strength)
 *   Vertex glow = near boundary pole (ρ < 0.15)
 *
 * Based on: Arkani-Hamed, Bai, Lam (2017) arXiv:1703.04541
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems — Positive Geometry Layer
 */

import React, { useMemo, useState, useCallback, useRef } from 'react';
import { computePositiveGeometry } from '../core/positive-geometry';

const DOMAIN_META = [
  { key: 'physical',   label: 'Cuerpo',     color: '#10b981', dims: ['sleep','nutrition','exercise','energy'] },
  { key: 'emotional',  label: 'Emociones',  color: '#f59e0b', dims: ['peace','gratitude','love','joy'] },
  { key: 'mental',     label: 'Mente',      color: '#6366f1', dims: ['clarity','focus','creativity','wisdom'] },
  { key: 'spiritual',  label: 'Espíritu',   color: '#a855f7', dims: ['faith','meditation','service','presence'] },
  { key: 'relational', label: 'Relaciones', color: '#ec4899', dims: ['family','friendship','community','compassion'] },
  { key: 'purpose',    label: 'Propósito',  color: '#0ea5e9', dims: ['meaning','mission','contribution','legacy'] },
  { key: 'alimento',   label: 'Alimento',   color: '#84cc16', dims: ['nourishment','taste_presence','food_harmony','gut_resonance'] },
];

const TAU = 2 * Math.PI;

// Λ_signal weights per domain (sum of LAMBDA_WEIGHTS for dims in each domain)
const DOMAIN_LAMBDA_WEIGHTS = {
  physical: 0.00,   // no signal dims
  emotional: 0.12,  // love(0.10) + gratitude(0.02)
  mental: 0.12,     // wisdom(0.08) + clarity(0.04)
  spiritual: 0.54,  // faith(0.17) + meditation(0.13) + presence(0.13) + service(0.11)
  relational: 0.08, // compassion(0.08)
  purpose: 0.06,    // meaning(0.06)
  alimento: 0.08,   // taste_presence(0.05) + food_harmony(0.03)
};

// 3D projection: rotate (x,y,z) by (rotX, rotY) and perspective project
function project3D(x, y, z, rotX, rotY) {
  const cosY = Math.cos(rotY), sinY = Math.sin(rotY);
  const cosX = Math.cos(rotX), sinX = Math.sin(rotX);
  const x1 = x * cosY - z * sinY;
  const z1 = x * sinY + z * cosY;
  const y2 = y * cosX - z1 * sinX;
  const z2 = y * sinX + z1 * cosX;
  const scale = 350 / (350 + z2);
  return { x: x1 * scale, y: y2 * scale, z: z2, scale };
}

// Compute inter-domain coherence (simplified: correlation of domain averages)
function computeDomainCoherence(state) {
  const domAvgs = DOMAIN_META.map(d => {
    const vals = d.dims.map(k => state[k] ?? 0.5);
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  });
  const mean = domAvgs.reduce((a, b) => a + b, 0) / 7;
  const pairs = [];
  for (let i = 0; i < 7; i++) {
    for (let j = i + 1; j < 7; j++) {
      // Coherence = 1 - |diff| normalized. High when both similar distance from mean.
      const diff = Math.abs(domAvgs[i] - domAvgs[j]);
      const coherence = Math.max(0, 1 - diff * 2);
      pairs.push({ i, j, coherence });
    }
  }
  return { domAvgs, pairs, mean };
}

export default function PolytopeVisualization({ state, lambda, lambdaSignal, width = 520, height = 380, t }) {
  const [rotation, setRotation] = useState({ x: 0.35, y: 0.6 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMouse, setLastMouse] = useState({ x: 0, y: 0 });
  const [hoveredVertex, setHoveredVertex] = useState(null);
  const svgRef = useRef(null);

  const tr = useCallback((key, fallback) => (t ? t(key, fallback) : fallback), [t]);

  const pg = useMemo(
    () => computePositiveGeometry(state, lambda, lambdaSignal),
    [state, lambda, lambdaSignal]
  );

  const domainData = useMemo(() => computeDomainCoherence(state), [state]);

  // Build 3D vertices for the 7 domains
  const vertices3D = useMemo(() => {
    return DOMAIN_META.map((d, i) => {
      const theta = (TAU * i / 7) - Math.PI / 2;
      const avg = domainData.domAvgs[i];
      const lambdaW = DOMAIN_LAMBDA_WEIGHTS[d.key] || 0;
      // Radius = domain average scaled
      const r = 40 + avg * 80; // 40..120
      // Height = Λ contribution (spiritual highest, physical lowest)
      const h = (lambdaW / 0.54) * 80 - 40; // -40..+40, normalized to spiritual max
      return {
        x3d: r * Math.cos(theta),
        y3d: h,
        z3d: r * Math.sin(theta),
        avg,
        lambdaW,
        domDist: pg.boundaryProximity.domainDistances[d.key] || 0.5,
        ...d,
        index: i,
      };
    });
  }, [domainData, pg]);

  // Mouse handlers for drag-to-rotate
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setLastMouse({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setRotation(r => ({
      x: r.x + (e.clientY - lastMouse.y) * 0.008,
      y: r.y + (e.clientX - lastMouse.x) * 0.008,
    }));
    setLastMouse({ x: e.clientX, y: e.clientY });
  }, [isDragging, lastMouse]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  // Project all vertices
  const projected = useMemo(() => {
    return vertices3D.map(v => ({
      ...v,
      ...project3D(v.x3d, v.y3d, v.z3d, rotation.x, rotation.y),
    }));
  }, [vertices3D, rotation]);

  // Center of SVG
  const cx = width * 0.5;
  const cy = height * 0.5;

  // Sort by z for painter's algorithm
  const sortedIndices = useMemo(() => {
    return projected.map((_, i) => i).sort((a, b) => projected[a].z - projected[b].z);
  }, [projected]);

  // Build edge elements (21 edges)
  const edges = useMemo(() => {
    return domainData.pairs.map(({ i, j, coherence }) => {
      const p1 = projected[i];
      const p2 = projected[j];
      const avgZ = (p1.z + p2.z) / 2;
      // Color: green (high coherence) → red (low coherence)
      const r = Math.round(239 * (1 - coherence) + 16 * coherence);
      const g = Math.round(68 * (1 - coherence) + 185 * coherence);
      const b = Math.round(68 * (1 - coherence) + 129 * coherence);
      return {
        x1: cx + p1.x, y1: cy + p1.y,
        x2: cx + p2.x, y2: cy + p2.y,
        color: `rgb(${r},${g},${b})`,
        opacity: 0.15 + coherence * 0.45,
        width: 1 + coherence * 2,
        z: avgZ,
        coherence,
        i, j,
      };
    }).sort((a, b) => a.z - b.z);
  }, [projected, domainData, cx, cy]);

  // Build triangulated facets (7 triangles: each domain with its two neighbors + center)
  const facets = useMemo(() => {
    const result = [];
    for (let i = 0; i < 7; i++) {
      const j = (i + 1) % 7;
      const pi = projected[i];
      const pj = projected[j];
      const avgZ = (pi.z + pj.z) / 2;
      // Facet color based on average of the two domains
      const avgVal = (domainData.domAvgs[i] + domainData.domAvgs[j]) / 2;
      const alpha = 0.04 + avgVal * 0.08;
      result.push({
        points: `${cx + pi.x},${cy + pi.y} ${cx + pj.x},${cy + pj.y} ${cx},${cy}`,
        fill: `rgba(192,132,252,${alpha})`,
        stroke: 'rgba(192,132,252,0.08)',
        z: avgZ,
      });
    }
    return result.sort((a, b) => a.z - b.z);
  }, [projected, domainData, cx, cy]);

  // Separatrix ring at Λ = 0.35 (horizontal plane)
  const separatrixPoints = useMemo(() => {
    const sepH = (0.35 / 0.54) * 80 - 40; // same height scale as vertices
    const pts = [];
    for (let i = 0; i <= 24; i++) {
      const theta = (TAU * i / 24);
      const r = 70; // fixed radius for separatrix
      const p = project3D(r * Math.cos(theta), sepH, r * Math.sin(theta), rotation.x, rotation.y);
      pts.push(`${cx + p.x},${cy + p.y}`);
    }
    return pts.join(' ');
  }, [rotation, cx, cy]);

  // Emergence zone ring at Λ = 0.65
  const emergencePoints = useMemo(() => {
    const emH = (0.65 / 0.54) * 80 - 40;
    const pts = [];
    for (let i = 0; i <= 24; i++) {
      const theta = (TAU * i / 24);
      const r = 55;
      const p = project3D(r * Math.cos(theta), emH, r * Math.sin(theta), rotation.x, rotation.y);
      pts.push(`${cx + p.x},${cy + p.y}`);
    }
    return pts.join(' ');
  }, [rotation, cx, cy]);

  // GCI display
  const gci = pg.gci;
  const gciCol = gci >= 0.70 ? '#10b981' : gci >= 0.45 ? '#fbbf24' : '#ef4444';
  const basin = pg.truncatedPolytope.basin;

  return (
    <div style={{ position: 'relative', width, margin: '0 auto' }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          background: '#08081a',
          borderRadius: 8,
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'block',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Separatrix ring (Λ=0.35) */}
        <polyline
          points={separatrixPoints}
          fill="none"
          stroke="#f59e0b"
          strokeWidth={1.2}
          strokeDasharray="5,3"
          opacity={0.4}
        />

        {/* Emergence zone ring (Λ=0.65) */}
        <polyline
          points={emergencePoints}
          fill="none"
          stroke="#10b981"
          strokeWidth={1}
          strokeDasharray="3,3"
          opacity={0.3}
        />

        {/* Facets (triangulated faces) */}
        {facets.map((f, i) => (
          <polygon
            key={`f${i}`}
            points={f.points}
            fill={f.fill}
            stroke={f.stroke}
            strokeWidth={0.5}
          />
        ))}

        {/* Edges (21 inter-domain connections) */}
        {edges.map((e, i) => (
          <line
            key={`e${i}`}
            x1={e.x1} y1={e.y1}
            x2={e.x2} y2={e.y2}
            stroke={e.color}
            strokeWidth={e.width}
            opacity={e.opacity}
          />
        ))}

        {/* Domain vertices (sorted back-to-front) */}
        {sortedIndices.map(idx => {
          const v = projected[idx];
          const isHovered = hoveredVertex === idx;
          const nearPole = v.domDist < 0.15;
          const r = (6 + v.avg * 10) * v.scale * (isHovered ? 1.4 : 1);

          return (
            <g key={`v${idx}`}>
              {/* Glow */}
              <circle
                cx={cx + v.x} cy={cy + v.y}
                r={r * 2.5}
                fill={nearPole ? 'rgba(239,68,68,0.15)' : `${v.color}15`}
              />
              {/* Vertex */}
              <circle
                cx={cx + v.x} cy={cy + v.y}
                r={r}
                fill={v.color}
                stroke={isHovered ? '#fff' : nearPole ? '#ef4444' : 'rgba(255,255,255,0.3)'}
                strokeWidth={isHovered ? 2.5 : nearPole ? 2 : 1}
                opacity={0.9}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredVertex(idx)}
                onMouseLeave={() => setHoveredVertex(null)}
              />
              {/* Label */}
              {v.scale > 0.6 && (
                <text
                  x={cx + v.x}
                  y={cy + v.y - r - 4}
                  textAnchor="middle"
                  fontSize={isHovered ? 10 : 8}
                  fontWeight={isHovered ? 'bold' : 'normal'}
                  fill={v.color}
                  opacity={0.9}
                  style={{ pointerEvents: 'none' }}
                >
                  {v.label}
                </text>
              )}
              {/* Near-pole warning */}
              {nearPole && (
                <circle
                  cx={cx + v.x} cy={cy + v.y}
                  r={r + 6}
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  strokeDasharray="3,2"
                  opacity={0.6}
                />
              )}
            </g>
          );
        })}

        {/* Center: GCI */}
        <text
          x={cx} y={cy - 4}
          textAnchor="middle"
          fontSize={18}
          fontWeight="bold"
          fill={gciCol}
          style={{ pointerEvents: 'none' }}
        >
          {gci.toFixed(2)}
        </text>
        <text
          x={cx} y={cy + 10}
          textAnchor="middle"
          fontSize={8}
          fill="#8888aa"
          style={{ pointerEvents: 'none' }}
        >
          GCI
        </text>

        {/* Emergence star if in ordered phase */}
        {lambda >= 0.65 && (
          <text
            x={cx} y={cy + 24}
            textAnchor="middle"
            fontSize={14}
            fill="#10b981"
            style={{ pointerEvents: 'none' }}
          >
            ✦
          </text>
        )}

        {/* Info panel (top-left) */}
        <g transform="translate(8, 8)">
          <rect x={0} y={0} width={155} height={72} rx={6} fill="rgba(0,0,0,0.75)" />
          <text x={8} y={16} fontSize={9} fontWeight="bold" fill="#C084FC">
            {tr('positiveGeometry.title', 'Consciousness Polytope')}
          </text>
          <text x={8} y={29} fontSize={7} fill="#8888aa">
            7 {tr('positiveGeometry.domains', 'domains')} × 4 dims = 28D → 3D
          </text>
          <text x={8} y={41} fontSize={7} fill="#8888aa">
            Ω={pg.canonicalForm.normalizedOmega.toFixed(2)} ρ={pg.boundaryProximity.globalMinDistance.toFixed(2)} F={pg.domainFactorizability.factorizability.toFixed(2)}
          </text>
          <text x={8} y={53} fontSize={7} fill={basin === 'A+' ? '#10b981' : '#ef4444'}>
            {tr('positiveGeometry.basin', 'Basin')}: {basin} | Λ={lambda.toFixed(2)}
          </text>
          <text x={8} y={65} fontSize={6} fill="#555577">
            Vol(P⁺)={(pg.truncatedPolytope.volumeRatio * 100).toFixed(1)}% | {pg.truncatedPolytope.activeFacets} {tr('positiveGeometry.facets', 'facets')}
          </text>
        </g>

        {/* Visual Legend (bottom-right) */}
        <g transform={`translate(${width - 145}, ${height - 80})`}>
          <rect x={-4} y={-6} width={142} height={76} rx={6} fill="rgba(0,0,0,0.7)" />
          {/* Sphere size */}
          <circle cx={8} cy={6} r={3} fill="#C084FC" opacity={0.8} />
          <circle cx={22} cy={6} r={6} fill="#C084FC" opacity={0.8} />
          <circle cx={40} cy={6} r={9} fill="#C084FC" opacity={0.8} />
          <text x={54} y={9} fontSize={7} fill="#aaa8c0">{tr('positiveGeometry.legendSize', 'Size = domain avg')}</text>
          {/* Red ring */}
          <circle cx={10} cy={24} r={6} fill="none" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="3,2" opacity={0.7} />
          <text x={22} y={27} fontSize={7} fill="#ef4444" opacity={0.8}>{tr('positiveGeometry.legendPole', 'ρ < 0.15 boundary pole')}</text>
          {/* Edge colors */}
          <line x1={4} y1={38} x2={18} y2={38} stroke="#10b981" strokeWidth={2} opacity={0.7} />
          <text x={22} y={41} fontSize={7} fill="#10b981" opacity={0.7}>{tr('positiveGeometry.legendEdgeGreen', 'High coherence')}</text>
          <line x1={4} y1={50} x2={18} y2={50} stroke="#ef4444" strokeWidth={2} opacity={0.7} />
          <text x={22} y={53} fontSize={7} fill="#ef4444" opacity={0.7}>{tr('positiveGeometry.legendEdgeRed', 'Low coherence')}</text>
          {/* Reference lines */}
          <line x1={4} y1={62} x2={18} y2={62} stroke="#f59e0b" strokeWidth={1.2} strokeDasharray="5,3" opacity={0.6} />
          <text x={22} y={65} fontSize={7} fill="#f59e0b" opacity={0.7}>Λ=0.35 sep.</text>
        </g>

        {/* Drag hint (bottom-center) */}
        <text x={width / 2} y={height - 6} textAnchor="middle" fontSize={7} fill="#444466">
          {tr('positiveGeometry.dragRotate', 'Drag to rotate')} · {tr('positiveGeometry.hoverDetails', 'Hover for details')}
        </text>
      </svg>

      {/* Tooltip */}
      {hoveredVertex !== null && projected[hoveredVertex] && (() => {
        const hv = hoveredVertex;
        const dm = DOMAIN_META[hv];
        const avg = domainData.domAvgs[hv];
        const lw = DOMAIN_LAMBDA_WEIGHTS[dm.key];
        const dd = projected[hv].domDist;
        const nearPole = dd < 0.15;
        return (
          <div style={{
            position: 'absolute',
            left: Math.min(cx + projected[hv].x + 16, width - 220),
            top: Math.max(cy + projected[hv].y - 80, 4),
            maxWidth: 220,
            padding: '10px 12px',
            background: 'rgba(10,10,30,0.95)',
            border: `1px solid ${dm.color}50`,
            borderRadius: 8,
            color: '#E0E0F0',
            fontSize: 10,
            lineHeight: 1.6,
            fontFamily: "'Inter', -apple-system, sans-serif",
            pointerEvents: 'none',
            zIndex: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          }}>
            <div style={{ fontWeight: 'bold', color: dm.color, fontSize: 12, marginBottom: 4 }}>
              {dm.label}
            </div>
            {/* Sphere size = avg */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ color: '#aaa8c0', fontSize: 9 }}>{tr('positiveGeometry.tipSphereSize', 'Sphere size')}:</span>
              <span style={{ fontWeight: 'bold', color: dm.color }}>{(avg * 100).toFixed(0)}%</span>
              <span style={{ fontSize: 8, color: '#666' }}>({tr('positiveGeometry.tipDomainAvg', 'domain avg')})</span>
            </div>
            {/* Height = Λ weight */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ color: '#aaa8c0', fontSize: 9 }}>{tr('positiveGeometry.tipHeight', 'Height')}:</span>
              <span style={{ fontWeight: 'bold', color: '#C084FC' }}>{(lw * 100).toFixed(0)}%</span>
              <span style={{ fontSize: 8, color: '#666' }}>({tr('positiveGeometry.tipLambdaWeight', 'Λ signal weight')})</span>
            </div>
            {/* Boundary proximity */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
              <span style={{ color: '#aaa8c0', fontSize: 9 }}>ρ:</span>
              <span style={{ fontWeight: 'bold', color: nearPole ? '#ef4444' : '#10b981' }}>{dd.toFixed(2)}</span>
              <span style={{ fontSize: 8, color: nearPole ? '#ef4444' : '#666' }}>
                {nearPole ? `⚠ ${tr('positiveGeometry.tipNearPole', 'Near boundary pole!')}` : tr('positiveGeometry.tipSafe', 'Safe')}
              </span>
            </div>
            {/* Individual dimensions as mini bars */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 4 }}>
              {dm.dims.map(d => {
                const val = state[d] ?? 0.5;
                const isExtreme = val < 0.10 || val > 0.90;
                return (
                  <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                    <span style={{ fontSize: 8, color: isExtreme ? '#ef4444' : '#8888aa', width: 80, textAlign: 'right' }}>{d}</span>
                    <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${val * 100}%`, height: '100%', background: isExtreme ? '#ef4444' : dm.color, borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: 8, fontWeight: 'bold', color: isExtreme ? '#ef4444' : '#ccc', width: 28, textAlign: 'right' }}>{(val * 100).toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
